function cloneTemplate(template) {
  if (!template || !template.content || !template.content.firstElementChild) return null;
  return template.content.firstElementChild.cloneNode(true);
}

const promotedTemplateStyles = new Set();
const promotedTemplateLinks = new Map();

function createStyleKey(text) {
  return text.trim();
}

function promoteTemplateStyle(styleElement) {
  const cssText = styleElement?.textContent || '';
  const styleKey = createStyleKey(cssText);

  if (!styleKey || promotedTemplateStyles.has(styleKey)) return;

  promotedTemplateStyles.add(styleKey);

  const style = document.createElement('style');
  style.dataset.hfPromotedTemplateStyle = 'true';
  style.textContent = cssText;
  document.head.appendChild(style);
}

function promoteTemplateLink(linkElement) {
  const href = linkElement?.getAttribute('href');

  if (!href) return Promise.resolve();
  if (promotedTemplateLinks.has(href)) return promotedTemplateLinks.get(href);

  const existingLink = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find(
    (link) => link.getAttribute('href') === href
  );

  if (existingLink) {
    const readyState = Promise.resolve();
    promotedTemplateLinks.set(href, readyState);
    return readyState;
  }

  const promise = new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.hfPromotedTemplateStyle = 'true';
    link.onload = () => resolve();
    link.onerror = reject;
    document.head.appendChild(link);
  });

  promotedTemplateLinks.set(href, promise);
  return promise;
}

function stripTemplateAssets(root) {
  if (!root?.querySelectorAll) return;

  root.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
    node.remove();
  });
}

function ensureTemplateAssets(template) {
  if (!template?.content) return Promise.resolve();

  const assetPromises = [];

  template.content.querySelectorAll('style').forEach((styleElement) => {
    promoteTemplateStyle(styleElement);
  });

  template.content.querySelectorAll('link[rel="stylesheet"]').forEach((linkElement) => {
    assetPromises.push(promoteTemplateLink(linkElement));
  });

  return Promise.all(assetPromises);
}

function preloadDesktopDrawerAssets(menu, configMap) {
  const promises = [];

  configMap.forEach((value) => {
    promises.push(ensureTemplateAssets(value.template));
  });

  getMenuItems(menu).forEach((item) => {
    const dropdownTemplate = getDropdownTemplate(item);

    if (dropdownTemplate) {
      promises.push(ensureTemplateAssets(dropdownTemplate));
    }
  });

  return Promise.all(promises);
}

function getDesktopMenus() {
  return Array.from(document.querySelectorAll('.header__inline-menu[data-hf-desktop-drawer="true"]'));
}

function getMenuItems(menu) {
  return Array.from(menu.querySelectorAll(':scope > .list-menu--inline > li[data-menu-link-url]'));
}

function getTriggerElement(item) {
  return item.querySelector(':scope > .hf-header-dropdown > .hf-header-dropdown__trigger, :scope > a');
}

function getDropdownTemplate(item) {
  return item.querySelector(':scope > .hf-header-dropdown > template[data-hf-dropdown-template]');
}

function buildMegamenuConfigMap(scope) {
  const map = new Map();

  scope.querySelectorAll('.header-megamenu-config[data-target-url]').forEach((config) => {
    const template = config.querySelector('template');
    if (!template || !template.content || !template.content.firstElementChild) return;

    map.set(config.dataset.targetUrl || '', {
      template,
      designMode: config.dataset.hfDesignMode === 'true',
    });
  });

  return map;
}

function setExpandedState(trigger, expanded) {
  if (!trigger) return;
  trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
}

function clearActiveItems(menu) {
  menu.querySelectorAll('[data-hf-drawer-active="true"]').forEach((item) => {
    item.removeAttribute('data-hf-drawer-active');
  });
}

function setActiveItem(menu, item) {
  clearActiveItems(menu);
  item.setAttribute('data-hf-drawer-active', 'true');
}

function positionDropdownContent(host, trigger) {
  const inner = host.viewport.querySelector('.hf-header-drawer-panel__inner--dropdown');
  const content = host.viewport.querySelector('.hf-header-drawer-dropdown-content');
  const pageWidth = host.viewport.querySelector('.page-width');

  if (!inner || !content || !pageWidth || !trigger) return;

  inner.style.setProperty('--hf-dropdown-offset-x', '0px');

  const triggerLabel = trigger.querySelector(':scope > span') || trigger;
  const labelRect = triggerLabel.getBoundingClientRect();
  const pageWidthRect = pageWidth.getBoundingClientRect();
  const pageWidthStyles = window.getComputedStyle(pageWidth);
  const pageWidthPaddingLeft = parseFloat(pageWidthStyles.paddingLeft || '0') || 0;

  let offset = labelRect.left - pageWidthRect.left - pageWidthPaddingLeft;
  const maxOffset = Math.max(0, pageWidth.clientWidth - content.offsetWidth);

  offset = Math.max(0, Math.min(offset, maxOffset));

  inner.style.setProperty('--hf-dropdown-offset-x', `${offset}px`);
}

function isInsideActiveRegion(host, { hoverOnly = false } = {}) {
  if (host.activeItem && host.activeItem.matches(':hover')) return true;
  if (host.drawer && host.drawer.matches(':hover')) return true;

  if (!hoverOnly) {
    const activeElement = document.activeElement;
    if (host.activeItem && activeElement && host.activeItem.contains(activeElement)) return true;
    if (host.drawer && activeElement && host.drawer.contains(activeElement)) return true;
  }

  return false;
}

function hasDrawerActivity(host) {
  return (
    host.wrapper?.dataset.hfDesktopDrawerOpen === 'true' ||
    host.wrapper?.dataset.hfDesktopDrawerClosing === 'true' ||
    !!host.activeUrl ||
    !!host.pendingOpenUrl ||
    !!host.drawer?.dataset.hfDrawerKind ||
    !!host.viewport?.children.length
  );
}

function clearPendingOpen(host) {
  if (host.openFrame1) {
    window.cancelAnimationFrame(host.openFrame1);
    host.openFrame1 = null;
  }

  if (host.openFrame2) {
    window.cancelAnimationFrame(host.openFrame2);
    host.openFrame2 = null;
  }

  host.pendingOpenUrl = null;
  host.pendingOpenKind = null;
}

function resetDrawerState(host, menu) {
  host.openRequestId += 1;

  if (host.activeTrigger) {
    setExpandedState(host.activeTrigger, false);
  }

  host.activeTrigger = null;
  host.activeItem = null;
  host.activeUrl = null;

  clearPendingOpen(host);
  clearActiveItems(menu);
}

async function openDrawerForItem(host, menu, item, configMap) {
  const normalizedUrl = item.dataset.menuLinkUrl || '';
  const trigger = getTriggerElement(item);

  if (!trigger) return;

  let nextContent = null;
  let kind = null;
  let sourceTemplate = null;

  const customConfig = configMap.get(normalizedUrl);
  const dropdownTemplate = getDropdownTemplate(item);

  if (customConfig) {
    sourceTemplate = customConfig.template;
    kind = 'megamenu';
  } else if (dropdownTemplate) {
    sourceTemplate = dropdownTemplate;
    kind = 'dropdown';
  }

  if (!sourceTemplate || !kind) return;

  const requestId = ++host.openRequestId;
  await ensureTemplateAssets(sourceTemplate);

  if (requestId !== host.openRequestId) return;

  nextContent = cloneTemplate(sourceTemplate);
  stripTemplateAssets(nextContent);

  if (!nextContent || !kind) return;

  const isSameActiveItem = host.activeUrl === normalizedUrl && host.drawer.dataset.hfDrawerKind === kind;
  const isPendingSameItem = host.pendingOpenUrl === normalizedUrl && host.pendingOpenKind === kind;

  if ((isSameActiveItem && host.wrapper.dataset.hfDesktopDrawerOpen === 'true') || isPendingSameItem) {
    if (kind === 'dropdown') {
      positionDropdownContent(host, trigger);
    }
    return;
  }

  if (host.wrapper.dataset.hfDesktopDrawerClosing === 'true') {
    if (host.closingListener) {
      host.drawer.removeEventListener('transitionend', host.closingListener);
      host.closingListener = null;
    }
    if (host.closingFallback) {
      window.clearTimeout(host.closingFallback);
      host.closingFallback = null;
    }
    delete host.wrapper.dataset.hfDesktopDrawerClosing;
  }

  clearPendingOpen(host);

  const wasAlreadyOpen = host.wrapper.dataset.hfDesktopDrawerOpen === 'true';

  if (host.activeTrigger && host.activeTrigger !== trigger) {
    setExpandedState(host.activeTrigger, false);
  }

  host.wrapper.style.setProperty(
    '--hf-header-bottom',
    `${Math.round(host.header.getBoundingClientRect().bottom)}px`
  );

  host.viewport.innerHTML = '';
  host.viewport.appendChild(nextContent);
  document.dispatchEvent(new CustomEvent('hf:megamenu:content-loaded', { detail: { scope: nextContent, kind: kind } }));

  setActiveItem(menu, item);
  setExpandedState(trigger, true);

  host.activeItem = item;
  host.activeTrigger = trigger;
  host.activeUrl = normalizedUrl;
  host.pendingOpenUrl = normalizedUrl;
  host.pendingOpenKind = kind;

  host.drawer.dataset.hfDrawerKind = kind;

  if (!wasAlreadyOpen) {
    host.drawer.getBoundingClientRect();
    host.openFrame1 = requestAnimationFrame(() => {
      host.openFrame1 = null;

      host.openFrame2 = requestAnimationFrame(() => {
        host.openFrame2 = null;
        host.wrapper.dataset.hfDesktopDrawerOpen = 'true';
        setScrollLock(true);
        clearPendingOpen(host);

        if (kind === 'dropdown') {
          positionDropdownContent(host, trigger);
        }
      });
    });

    return;
  }

  if (kind === 'dropdown') {
    positionDropdownContent(host, trigger);
  }
}

function setScrollLock(actif) {
  const corps = document.body;
  const verrouille = corps.classList.contains('hf-header-scroll-lock');

  if (actif === verrouille) return;

  if (actif) {
    const largeurBarre = window.innerWidth - document.documentElement.clientWidth;
    if (largeurBarre > 0) corps.style.paddingRight = `${largeurBarre}px`;
    corps.classList.add('hf-header-scroll-lock');
  } else {
    corps.classList.remove('hf-header-scroll-lock');
    corps.style.paddingRight = '';
  }
}

function closeDrawer(host, menu) {
  if (!host.wrapper || !host.drawer || !host.viewport) return;
  if (!hasDrawerActivity(host)) return;

  const isOpen = host.wrapper.dataset.hfDesktopDrawerOpen === 'true';
  const isClosing = host.wrapper.dataset.hfDesktopDrawerClosing === 'true';

  if (!isOpen && !isClosing) {
    resetDrawerState(host, menu);
    host.viewport.innerHTML = '';
    delete host.drawer.dataset.hfDrawerKind;
    return;
  }

  delete host.wrapper.dataset.hfDesktopDrawerOpen;
  setScrollLock(false);
  host.wrapper.dataset.hfDesktopDrawerClosing = 'true';
  resetDrawerState(host, menu);

  if (host.closingListener) {
    host.drawer.removeEventListener('transitionend', host.closingListener);
  }
  if (host.closingFallback) {
    window.clearTimeout(host.closingFallback);
  }

  const finishClosing = () => {
    host.drawer.removeEventListener('transitionend', host.closingListener);
    window.clearTimeout(host.closingFallback);
    host.closingListener = null;
    host.closingFallback = null;
    delete host.wrapper.dataset.hfDesktopDrawerClosing;
  };

  host.closingListener = (event) => {
    if (event.propertyName !== 'grid-template-rows') return;
    finishClosing();
  };

  host.drawer.addEventListener('transitionend', host.closingListener);
  host.closingFallback = window.setTimeout(finishClosing, 420);

  window.setTimeout(() => {
    if (host.wrapper.dataset.hfDesktopDrawerOpen === 'true') return;
    host.viewport.innerHTML = '';
    delete host.drawer.dataset.hfDrawerKind;
  }, 380);
}

function scheduleDrawerClose(host, menu) {
  if (
    window.Shopify &&
    window.Shopify.designMode &&
    host.activeUrl &&
    host.configMap &&
    host.configMap.get(host.activeUrl)?.designMode
  ) {
    return;
  }
  if (!hasDrawerActivity(host)) return;

  window.clearTimeout(host.closeTimeout);

  host.closeTimeout = window.setTimeout(() => {
    if (isInsideActiveRegion(host, { hoverOnly: host._closeHoverOnly })) return;
    closeDrawer(host, menu);
  }, 180);
}

function cancelDrawerClose(host) {
  window.clearTimeout(host.closeTimeout);
  host._closeHoverOnly = false;
}

function bindMenuItem(host, menu, item, configMap) {
  if (item.dataset.hfDesktopDrawerBound === 'true') return;

  const normalizedUrl = item.dataset.menuLinkUrl || '';
  const trigger = getTriggerElement(item);

  if (!trigger) return;

  const hasCustomMegamenu = configMap.has(normalizedUrl);
  const hasDropdown = !!getDropdownTemplate(item);

  if (!hasCustomMegamenu && !hasDropdown) return;

  const openCurrentItem = () => {
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    cancelDrawerClose(host);
    openDrawerForItem(host, menu, item, configMap);
  };

  item.addEventListener('mouseenter', openCurrentItem);

  trigger.addEventListener('focus', openCurrentItem);

  trigger.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      openCurrentItem();
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer(host, menu);
      trigger.focus();
    }
  });

  trigger.addEventListener('click', (event) => {
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    event.preventDefault();
    openCurrentItem();
  });

  item.dataset.hfDesktopDrawerBound = 'true';
}

function initDesktopDrawerForMenu(menu) {
  const wrapper = menu.closest('.header-wrapper');
  const header = wrapper?.querySelector(':scope > .header') || wrapper?.querySelector('.header');
  const drawer = wrapper?.querySelector('[data-hf-header-desktop-drawer]');
  const viewport = wrapper?.querySelector('[data-hf-header-desktop-drawer-viewport]');

  if (!wrapper || !header || !drawer || !viewport) return;

  wrapper.style.setProperty('--hf-header-bottom', `${Math.round(wrapper.getBoundingClientRect().bottom)}px`);

  const host = {
    wrapper,
    header,
    drawer,
    viewport,
    configMap: null,
    closeTimeout: null,
    closingListener: null,
    closingFallback: null,
    openFrame1: null,
    openFrame2: null,
    openRequestId: 0,
    activeTrigger: null,
    activeItem: null,
    activeUrl: null,
    pendingOpenUrl: null,
    pendingOpenKind: null,
  };

  const configMap = buildMegamenuConfigMap(wrapper);
  host.configMap = configMap;
  const menuItems = getMenuItems(menu);

  preloadDesktopDrawerAssets(menu, configMap);

  menuItems.forEach((item) => {
    bindMenuItem(host, menu, item, configMap);
  });

  wrapper.addEventListener('mouseenter', () => {
    cancelDrawerClose(host);
  });

  wrapper.addEventListener('mouseleave', () => {
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    host._closeHoverOnly = true;
    scheduleDrawerClose(host, menu);
  });

  wrapper.addEventListener('focusin', () => {
    cancelDrawerClose(host);
  });

  wrapper.addEventListener('focusout', () => {
    if (!window.matchMedia('(min-width: 990px)').matches) return;

    window.setTimeout(() => {
      if (!wrapper.contains(document.activeElement)) {
        host._closeHoverOnly = false;
        scheduleDrawerClose(host, menu);
      }
    });
  });

  window.addEventListener('resize', () => {
    if (wrapper.dataset.hfDesktopDrawerOpen !== 'true') return;

    if (!window.matchMedia('(min-width: 990px)').matches) {
      closeDrawer(host, menu);
      return;
    }

    if (drawer.dataset.hfDrawerKind === 'dropdown' && host.activeTrigger) {
      positionDropdownContent(host, host.activeTrigger);
    }
  }, { signal: _megamenuAbort?.signal });

  document.addEventListener('keyup', (event) => {
    if (event.key === 'Escape') {
      closeDrawer(host, menu);
    }
  }, { signal: _megamenuAbort?.signal });

  const designModeEntry = Array.from(configMap.entries()).find(([, value]) => value.designMode);

  if (designModeEntry && window.matchMedia('(min-width: 990px)').matches) {
    const designModeUrl = designModeEntry[0];
    const designModeItem = menuItems.find((item) => (item.dataset.menuLinkUrl || '') === designModeUrl);

    if (designModeItem) {
      openDrawerForItem(host, menu, designModeItem, configMap);
    }
  }
}

let _megamenuAbort = null;

function initCustomHeaderMegamenu() {
  if (_megamenuAbort) _megamenuAbort.abort();
  _megamenuAbort = new AbortController();

  getDesktopMenus().forEach((menu) => {
    initDesktopDrawerForMenu(menu);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomHeaderMegamenu, { once: true });
} else {
  initCustomHeaderMegamenu();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target?.querySelector('.header-wrapper')) {
    initCustomHeaderMegamenu();
  }
});

document.addEventListener('click', (e) => {
  const toggle = e.target.closest('.hf-header-drawer-dropdown-toggle');
  if (!toggle) return;

  const row = toggle.closest('.hf-header-drawer-dropdown-row');
  if (!row) return;

  const collapsible = row.nextElementSibling;
  if (!collapsible || !collapsible.classList.contains('hf-header-drawer-dropdown-collapsible')) return;

  const isOpen = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
  collapsible.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
});

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('.hf-header-drawer-links__trigger');
  if (!trigger) return;

  const collapsible = trigger.nextElementSibling;
  if (!collapsible || !collapsible.classList.contains('hf-header-drawer-links__collapsible')) return;

  const isOpen = trigger.getAttribute('aria-expanded') === 'true';
  trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
  collapsible.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
});